import { Directive, inject } from '@angular/core';
import { Store } from '@sama/app-state';
import { DirectiveSuperclass } from '@sama/ng-core';
import {
  getShortcutName,
  toolNameForEventTracking,
} from '../../../../cross-team-shared/annotation/shared/abstract-tool';
import { ToggleButton } from '../../../../cross-team-shared/components/toggle-button-group/toggle-button-group.component';
import { KeyboardShortcutService } from '../../../../cross-team-shared/misc-services/keyboard-shortcut.service';
import { ToolsHelperService } from '../../../../cross-team-shared/misc-services/tools-helper.service';
import {
  EventTrackingValueWithSource,
  humanize,
} from '../../../../cross-team-shared/misc-utils';
import { AbstractSensorContentStore } from '../../../../cross-team-shared/state/abstract-sensor-content-store';
import { ToolName } from '../../../../cross-team-shared/tools-names';
import {
  ToolConfig,
  toolsConfig,
} from '../../../../cross-team-shared/tools.config';
import { shellConnectorToken } from '../../../../team-apis/core/shell-connector';
import { ToolManager2DService } from '../../tools/shared/tool-manager-2d.service';

@Directive()
export abstract class AbstractMainToolbar2DComponent extends DirectiveSuperclass {
  buttons: Array<ToggleButton<ToolName>> = [];
  selectedToolStore!: Store<ToolName>;
  toolsHelperService = inject(ToolsHelperService);

  protected store = inject(AbstractSensorContentStore);
  private keyboardShortcutService = inject(KeyboardShortcutService);
  private shellConnector = inject(shellConnectorToken);
  private toolManager = inject(ToolManager2DService);

  constructor() {
    super();

    this.selectedToolStore = this.store('selectedTool');
    this.configureButtons();
  }

  selectTool(name: ToolName, eventTrackingValue: EventTrackingValueWithSource) {
    if (this.selectedToolStore.state() === name) {
      return;
    }

    this.selectedToolStore.set(name);
    this.shellConnector.trackEvent({
      id: 'select_main_tool',
      bucket: this.getTrackingEventBucket(name),
      ...((name === 'Measure' || name === 'Zoom') && { sub_bucket: 'frame' }),
      ...eventTrackingValue,
      tool: toolNameForEventTracking(name),
    });
  }

  protected buildButtonFromTool(
    toolConfig: ToolConfig<ToolName>,
  ): ToggleButton<ToolName> {
    return {
      value: toolConfig.name,
      ...toolConfig,
      tooltip: this.getTooltip(toolConfig.name),
    };
  }

  private getTrackingEventBucket(name: ToolName) {
    switch (name) {
      case 'Measure':
      case 'Zoom':
        return 'navigation';
      case 'Edit':
      case 'Select':
        return 'adjustment';
      default:
        return 'annotation';
    }
  }

  private configureButtons() {
    const toolNames = this.getOrderedToolNames();
    toolNames.forEach((toolName) => {
      this.setupKeyboardShortcut(toolName);
      this.toolManager.initTool(toolName);
      this.buttons.push(this.buildButtonFromTool(toolsConfig[toolName]));
    });
    this.selectedToolStore.set(toolNames[0]);
  }

  private getTooltip(toolName: ToolName) {
    const readableShortcut = this.keyboardShortcutService.getTooltipSuffix(
      getShortcutName(toolName),
    );
    if (!readableShortcut) {
      return humanize(toolName);
    }
    return humanize(toolName) + readableShortcut;
  }

  private setupKeyboardShortcut(toolName: ToolName) {
    this.subscribeTo(
      this.keyboardShortcutService.getMatchingEvent$(getShortcutName(toolName)),
      ({ eventTrackingValue }) => {
        this.selectTool(toolName, eventTrackingValue);
      },
    );
  }

  protected abstract getOrderedToolNames(): ToolName[];
}
